import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { DataTableDirective } from 'angular-datatables';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { DocumentosElectronicosService } from 'src/app/services/transacciones/documentos-electronicos.service';
import { LanguageApp } from '../../laboratorio/laboratorios/laboratorios.component';

@Component({
  selector: 'app-notas-credito',
  templateUrl: './notas-credito.component.html',
  styleUrls: ['./notas-credito.component.scss'],
  standalone: false
})
export class NotasCreditoComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(DataTableDirective, { static: false }) dtElement!: DataTableDirective;
  @ViewChild('rideModal', { static: false }) rideModal?: TemplateRef<any>;

  dtOptions: DataTables.Settings = {};
  dtTrigger: Subject<any> = new Subject();
  isDtInitialized = false;

  public notas: any[] = [];
  public spinnerBoolean = false;
  public fechaInicio = '';
  public fechaFin = '';
  public generandoRide = false;
  public isMobileView = false;
  public rideUrl: SafeResourceUrl = '';
  public rideModalRef?: NgbModalRef;
  public enviandoSRI: { [codigo: string]: boolean } = {};

  private mobileTablesObserver?: MutationObserver;
  private mobileLabelsRaf?: number;

  get token(): string { return localStorage.getItem('token') || ''; }

  constructor(
    private documentosElectronicosService: DocumentosElectronicosService,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private elementRef: ElementRef,
    private modalService: NgbModal,
    private sanitizer: DomSanitizer,
  ) {}

  @HostListener('window:resize')
  onWindowResize(): void {
    this.detectMobileView();
    this.updateMobileTableLabels();
  }

  ngOnInit(): void {
    const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Guayaquil' });
    this.fechaInicio = hoy;
    this.fechaFin = hoy;
    this.detectMobileView();
    this.buildDtOptions();
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
    this.isDtInitialized = true;
    this.initializeMobileTables();
  }

  ngOnDestroy(): void {
    this.dtTrigger.unsubscribe();
    this.mobileTablesObserver?.disconnect();
    if (this.mobileLabelsRaf) {
      cancelAnimationFrame(this.mobileLabelsRaf);
    }
  }

  private detectMobileView(): void {
    this.isMobileView = typeof window !== 'undefined' && window.innerWidth <= 992;
  }

  private initializeMobileTables(): void {
    if (typeof window === 'undefined') return;
    this.updateMobileTableLabels();
    this.ngZone.runOutsideAngular(() => {
      if (typeof MutationObserver === 'undefined') return;
      this.mobileTablesObserver?.disconnect();
      this.mobileTablesObserver = new MutationObserver(() => this.updateMobileTableLabels());
      this.mobileTablesObserver.observe(this.elementRef.nativeElement, { childList: true, subtree: true });
    });
  }

  private updateMobileTableLabels(): void {
    if (typeof window === 'undefined') return;
    if (this.mobileLabelsRaf) cancelAnimationFrame(this.mobileLabelsRaf);
    this.mobileLabelsRaf = window.requestAnimationFrame(() => {
      const hostElement = this.elementRef.nativeElement as HTMLElement;
      const tables = Array.from(hostElement.querySelectorAll<HTMLTableElement>('.mobile-friendly-table'));
      tables.forEach((table) => {
        const headers = Array.from(table.querySelectorAll<HTMLTableCellElement>('thead th')).map((th) => th.textContent?.trim() ?? '');
        table.querySelectorAll('tbody tr').forEach((row) => {
          row.querySelectorAll<HTMLTableCellElement>('td').forEach((cell, index) => {
            const headerText = headers[index];
            if (headerText && cell.getAttribute('data-label') !== headerText) {
              cell.setAttribute('data-label', headerText);
            }
          });
        });
      });
    });
  }

  aplicarFiltros(): void {
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  private buildDtOptions(): void {
    this.dtOptions = {
      language: LanguageApp.spanish_datatables,
      pagingType: 'full_numbers',
      pageLength: 10,
      serverSide: true,
      processing: true,
      responsive: true,
      orderClasses: false,
      searchDelay: 1500,
      ajax: (dtParams: any, callback: any) => {
        this.ngZone.run(() => { this.notas = []; this.spinnerBoolean = true; });
        this.documentosElectronicosService.listarNotasCredito(this.token, {
          fechaInicio: this.fechaInicio,
          fechaFin: this.fechaFin,
          start: dtParams.start,
          length: dtParams.length,
          search: dtParams.search?.value || '',
        }).subscribe((resp: any) => {
          this.ngZone.run(() => {
            this.notas = Array.isArray(resp?.data) ? resp.data : [];
            this.spinnerBoolean = false;
            callback({
              recordsTotal: resp?.recordsTotal || 0,
              recordsFiltered: resp?.recordsFiltered || 0,
              data: [],
            });
            this.updateMobileTableLabels();
          });
        });
      },
    };
  }

  formatFecha(fecha: string): string {
    if (!fecha) return '';
    try {
      const d = new Date(fecha);
      const tz = 'America/Guayaquil';
      const dia = d.toLocaleDateString('en-CA', { timeZone: tz }).split('-').reverse().join('/');
      const hora = d.toLocaleTimeString('en-GB', { timeZone: tz, hour12: false }).slice(0, 5);
      return `${dia} ${hora}`;
    } catch { return fecha; }
  }

  getEstadoBadgeClass(estado: string): string {
    const map: { [key: string]: string } = {
      activo: 'bg-success',
      pendiente: 'bg-warning',
      anulado: 'bg-danger'
    };
    return map[estado?.toLowerCase()] || 'bg-secondary';
  }

  getEstadoLabel(estado: string): string {
    const map: { [key: string]: string } = {
      activo: 'Autorizado',
      pendiente: 'Pendiente',
      anulado: 'Anulado'
    };
    return map[estado?.toLowerCase()] || estado;
  }

  shouldShowCheck(campo: string, nota: any): boolean {
    if (campo === 'enviadoCliente') {
      const count = [nota.firmado, nota.enviado, nota.autorizado, nota.enviadoCliente].filter(v => v === '1' || v === 1).length;
      return count >= 3 || nota.enviadoCliente === '1' || nota.enviadoCliente === 1;
    }
    return nota[campo] === '1' || nota[campo] === 1;
  }

  puedeEnviarSRI(nota: any): boolean {
    return !!nota?.claveFactura && !this.shouldShowCheck('autorizado', nota);
  }

  estaEnviandoSRI(nota: any): boolean {
    return !!this.enviandoSRI[String(nota?.COD_TRAC || '')];
  }

  enviarNotaSRI(nota: any): void {
    if (!nota?.claveFactura) {
      this.toastr.error('No se encontró clave de acceso');
      return;
    }

    const codigoNota = String(nota.COD_TRAC || '');
    if (this.enviandoSRI[codigoNota]) {
      return;
    }

    this.enviandoSRI[codigoNota] = true;
    this.spinnerBoolean = true;
    const emailCliente = nota.email_cliente || nota.EMA_CLI || '';
    const nombreCliente = nota.nombre_cliente?.trim() ? nota.nombre_cliente : nota.NOM_CLI;

    this.documentosElectronicosService.enviarNotaCreditoSRI(
      nota.claveFactura,
      emailCliente,
      nombreCliente || '',
      this.token
    ).subscribe({
      next: (resp: any) => {
        this.spinnerBoolean = false;
        delete this.enviandoSRI[codigoNota];
        const isOldFormat = resp && typeof resp === 'object' &&
          resp['7'] && typeof resp['7'] === 'string' &&
          resp['7'].toLowerCase().includes('mail');
        const success = resp?.success === true || isOldFormat;

        if (success) {
          this.toastr.success('Nota de crédito enviada al SRI correctamente');
        } else {
          this.toastr.warning('El SRI no autorizó todavía la nota de crédito');
        }

        this.recargarTabla();
      },
      error: (err) => {
        this.spinnerBoolean = false;
        delete this.enviandoSRI[codigoNota];
        this.toastr.error('No se pudo enviar al SRI: ' + (err?.error?.error || err?.error?.message || 'Error desconocido'));
      }
    });
  }

  private recargarTabla(): void {
    if (this.isDtInitialized) {
      this.dtElement?.dtInstance?.then(dt => dt.ajax.reload());
    }
  }

  generarRide(nota: any): void {
    if (!nota?.claveFactura) {
      this.toastr.error('No se encontró clave de acceso');
      return;
    }

    this.generandoRide = true;
    this.documentosElectronicosService.generarRide(this.token, nota.claveFactura, nota.estadoFactura)
      .subscribe({
        next: (blob: Blob) => {
          const url = window.URL.createObjectURL(blob);
          if (this.isMobileView && this.rideModal) {
            this.rideUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            this.rideModalRef = this.modalService.open(this.rideModal, {
              size: 'lg',
              centered: true,
              scrollable: true
            });
          } else {
            window.open(url, '_blank');
          }
          this.generandoRide = false;
          this.toastr.success('RIDE generado correctamente');
        },
        error: (err) => {
          this.generandoRide = false;
          this.toastr.error('Error al generar el RIDE: ' + (err?.error?.error || 'desconocido'));
        }
      });
  }

  exportarExcel(): void {
    this.spinnerBoolean = true;
    this.documentosElectronicosService.exportarExcelNotasCredito(this.token, {
      fechaInicio: this.fechaInicio,
      fechaFin: this.fechaFin,
    }).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `notas-credito-${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        this.spinnerBoolean = false;
        this.toastr.success('Excel exportado correctamente');
      },
      error: (err) => {
        this.spinnerBoolean = false;
        this.toastr.error('Error al exportar: ' + (err?.error?.error || 'desconocido'));
      }
    });
  }
}
